import React, { useState } from 'react';
import { Row, Col, Form, Button, Pagination } from 'react-bootstrap';
import { FaCalendar } from "react-icons/fa";
import { utils, writeFile } from 'xlsx'; // For Excel export
import jsPDF from 'jspdf'; // For PDF export
import 'jspdf-autotable'; // For generating tables in PDFs
import BradFactorLayouts from '../../components/BradFactorLayout.jsx'; 
import { TbLoader3 } from "react-icons/tb"; // Importing TbLoader3
import './view-bradford.css'; // Ensure the path is correct

export default function ViewBradFord() {
  const [startDate, setStartDate] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [showTable, setShowTable] = useState(false);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false); // State for generating report
  const [reportGenerated, setReportGenerated] = useState(false); // State to check if report is generated
  const rowsPerPage = 10;

  const handleGenerateReport = () => {
    // Perform validation
    if (!startDate) {
      setFormErrors({ startDate: 'Start date is required.' });
      return;
    }

    // Clear errors
    setFormErrors({});

    // Set generating state
    setIsGenerating(true);

    // Simulate data fetching with a timeout
    setTimeout(() => {
      // Fetch or calculate report data
      const fetchedData = Array.from({ length: 30 }, (_, index) => ({
        date: `2024-08-0${(index % 30) + 1}`,
        checkIn: '09:00',
        checkOut: '17:00',
        workingHours: '8',
        overtime: '0',
        overtimeStatus: 'N/A',
        status: 'Present'
      }));

      setData(fetchedData);
      setShowTable(true);
      setIsGenerating(false); // Reset generating state
      setReportGenerated(true); // Mark report as generated
    }, 2000); // Simulating a 2-second loading time
  };

  const exportToExcel = () => {
    const worksheet = utils.json_to_sheet(data); // Convert JSON data to Excel sheet format
    const workbook = utils.book_new(); // Create a new workbook
    utils.book_append_sheet(workbook, worksheet, 'Attendance Report'); // Append the worksheet to the workbook
    writeFile(workbook, 'AttendanceReport.xlsx'); // Save the Excel file
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Bradford Score', 20, 10);
    doc.autoTable({
      head: [['Date', 'CheckIn', 'CheckOut', 'Worked Hours', 'Overtime', 'Overtime Status', 'Status']],
      body: data.map(item => [item.date, item.checkIn, item.checkOut, item.workingHours, item.overtime, item.overtimeStatus, item.status]),
    });
    doc.save('AttendanceReport.pdf'); // Save the PDF
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= Math.ceil(data.length / rowsPerPage)) {
      setCurrentPage(pageNumber);
    }
  };

  const pageRange = () => {
    const pages = [];
    const totalPages = Math.ceil(data.length / rowsPerPage);
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const displayedData = data.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  return (
    <BradFactorLayouts>
      <h1 className='view-bradford-title'><FaCalendar /> Bradford of 2024</h1>
      <hr />
      <Form className='view-bradford-form'>
        <Row>
          <Col md={6} xs={12}>
            <Form.Group>
              <Form.Label>Start date <span className='required'>*</span> :</Form.Label>
              <Form.Control
                type="date"
                id="view-bradford_month"
                name="view-bradford-month"
                required
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                isInvalid={!!formErrors.startDate}
              />
              <Form.Control.Feedback type="invalid">{formErrors.startDate}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Button
          className="generate-report-button"
          type="button"
          onClick={handleGenerateReport}
          disabled={isGenerating || reportGenerated} // Disable button if generating or report is already generated
        >
          {isGenerating ? (
            <>
              <TbLoader3 className="icon-rotate" />
              Generating...
            </>
          ) : (
            'Generate Report'
          )}
        </Button>
      </Form>

      {showTable && (
        <div className='view-bradford-table-container'>
          <div className='table-actions'>
            <Row className='w-100'>
              <Col xs={12} md={6} className='d-flex align-items-center mb-3 justify-content-start'>
                <Button variant="secondary" className='export-button' onClick={exportToExcel}>Excel</Button>
                <Button variant="secondary" className='export-button' onClick={exportToPDF}>PDF</Button>
              </Col>
              <Col xs={12} md={6} className='d-flex align-items-center mb-3 justify-content-end'>
                <Form className="d-flex justify-content-end">
                  <Form.Control
                    type="search"
                    placeholder="Search"
                    aria-label="Search"
                    className="search-bar"
                  />
                </Form>
              </Col>
            </Row>
          </div>
          <div className="table-responsive">
            <table className='table table-striped'>
              <thead className='view-bradford-table-header'>
                <tr>
                  <th>SR</th>
                  <th>Date</th>
                  <th>CheckIn</th>
                  <th>CheckOut</th>
                  <th>Worked Hours</th>
                  <th>Overtime</th>
                  <th>Overtime Status</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {displayedData.map((item, index) => (
                  <tr key={index} style={{ textAlign: 'center' }}>
                    <td>{index + 1}</td>
                    <td>{item.date}</td>
                    <td>{item.checkIn}</td>
                    <td>{item.checkOut}</td>
                    <td>{item.workingHours}</td>
                    <td>{item.overtime}</td>
                    <td>{item.overtimeStatus}</td>
                    <td>{item.status}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className='table-footer'>
                <tr className='view-bradford-table-footer'>
                  <td colSpan="8" style={{ textAlign: 'left' }}>
                    Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, data.length)} of {data.length} entries
                  </td>
                </tr>
              </tfoot>
            </table>
            <div className="pagination-container">
              <Row className="custom-row">
                <Col xs={12} md={9} className="custom-col-left">
                  Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, data.length)} of {data.length} entries
                </Col>
                <Col xs={12} md={3} className="custom-col-right">
                  <Pagination>
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Pagination.Prev>
                    {pageRange().map(page => (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === Math.ceil(data.length / rowsPerPage)}
                    >
                      Next
                    </Pagination.Next>
                  </Pagination>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      )}
    </BradFactorLayouts>
  );
}
